import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from '@uzurv/afp/button/module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MobilityProfileDetail } from '@uzurv/clara';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogModule } from '../dialogs/confirmation-dialog/confirmation-dialog.module';
import { ConfirmationDialogComponent } from '../dialogs/confirmation-dialog/confirmation-dialog.component';
import { isNullOrUndefined } from '@uzurv/core';
import {
  CheckboxFormGroup,
  CheckboxGroup,
  CheckboxOption,
  CheckboxGroupOutput,
  AdditionalOption
} from '../forms/checkbox-form-group/checkbox-form-group';

@Component({
  selector: 'app-rider-mobility-profile',
  templateUrl: './rider-mobility-profile.component.html',
  styleUrls: ['./rider-mobility-profile.component.scss'],
  imports: [CommonModule, ButtonModule, ConfirmationDialogModule, CheckboxFormGroup],
  standalone: true
})
export class RiderMobilityProfileComponent implements OnInit {
  constructor(private snackBar: MatSnackBar, private dialog: MatDialog) {}
  @Input() mobilityProfileDetail: MobilityProfileDetail;
  @Input() hasEditPermission = false;
  @Input() disableFormSubmitButtons = false;
  @Output() mobilityProfileSaved = new EventEmitter<MobilityProfileDetail>();

  mobilityProfileSectionForms: Record<number, CheckboxGroupOutput> = {};
  mobilityProfileSections: Array<CheckboxGroup> = [];
  showValidationErrors = false;

  get isProfileDirty(): boolean {
    return Object.values(this.mobilityProfileSectionForms).some(form => form.dirty);
  }

  get isProfileValid(): boolean {
    return Object.values(this.mobilityProfileSectionForms).every(form => form.valid);
  }

  ngOnInit(): void {
    this.mobilityProfileSections = this.convertMobilityProfileDetailToSections(this.mobilityProfileDetail);
  }

  onMobilityProfileChanged(index: number, event: CheckboxGroupOutput) {
    this.mobilityProfileSectionForms[index] = event;
  }

  handleDiscardChanges() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Discard Changes',
        message: 'Are you sure you want to discard changes?',
        confirmText: 'Discard',
        dismissText: 'Cancel',
        isDestructive: true
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.mobilityProfileSectionForms = {};
        this.mobilityProfileSections = this.convertMobilityProfileDetailToSections(this.mobilityProfileDetail);
      }
    });
  }

  handleSaveChanges() {
    if (!this.isProfileValid) {
      this.showValidationErrors = true;
    } else {
      const updatedMobilityProfile = this.convertMobilityProfileSectionToDetail(this.mobilityProfileSectionForms);
      this.mobilityProfileSaved.emit(updatedMobilityProfile);
      this.showValidationErrors = false;
      Object.keys(this.mobilityProfileSectionForms).forEach(key => {
        this.mobilityProfileSectionForms[key].dirty = false;
      });
    }
  }

  convertMobilityProfileDetailToSections(mobilityProfileDetail: MobilityProfileDetail): Array<CheckboxGroup> {
    const mobilityAids: Array<CheckboxOption> = [
      { label: 'Manual Wheelchair', name: 'manualWheelchair' },
      { label: 'Power Wheelchair', name: 'powerWheelchair' },
      { label: 'Electric Scooter', name: 'electricScooter' },
      { label: 'Bariatric Wheelchair', name: 'bariatricWheelchair' },
      { label: 'Walker', name: 'hasWalker' },
      { label: 'Cane', name: 'hasCane' },
      { label: 'Rollator', name: 'hasRollator' }
    ].map(item => {
      if (item.name === 'manualWheelchair' || item.name === 'electricScooter') {
        return {
          label: item.label,
          name: item.name,
          checked: !isNullOrUndefined(mobilityProfileDetail?.[item.name]),
          additionalOptions: [
            {
              name: 'transferToSeat',
              label: 'Can the rider transfer into a car with little or no assistance?',
              type: 'radio',
              options: [
                { label: 'Yes', value: 'yes', checked: !mobilityProfileDetail[item.name]?.seatTransferAssistanceRequired },
                { label: 'No', value: 'no', checked: mobilityProfileDetail[item.name]?.seatTransferAssistanceRequired }
              ]
            },
            {
              name: 'liftOrRamp',
              label: 'Does the rider require or have a preference between a lift or ramp loading vehicle?',
              type: 'radio',
              showIfOptionValue: [{ name: 'transferToSeat', value: 'no' }],
              options: [
                { label: 'Requires ramp', value: 'RAMP', checked: mobilityProfileDetail[item.name]?.vehicleEquipmentType === 'RAMP' },
                { label: 'Requires lift', value: 'LIFT', checked: mobilityProfileDetail[item.name]?.vehicleEquipmentType === 'LIFT' },
                {
                  label: 'No preference',
                  value: 'NOPREF',
                  checked: !mobilityProfileDetail.creationNeeded && mobilityProfileDetail[item.name]?.vehicleEquipmentType === null
                }
              ]
            }
          ]
        };
      }

      if (item.name === 'bariatricWheelchair' || item.name === 'powerWheelchair') {
        return {
          label: item.label,
          name: item.name,
          checked: !isNullOrUndefined(mobilityProfileDetail?.[item.name]),
          additionalOptions: [
            {
              name: 'liftOrRamp',
              label: 'Does the rider require or have a preference between a lift or ramp loading vehicle?',
              type: 'radio',
              options: [
                { label: 'Requires ramp', value: 'RAMP', checked: mobilityProfileDetail[item.name]?.vehicleEquipmentType === 'RAMP' },
                { label: 'Requires lift', value: 'LIFT', checked: mobilityProfileDetail[item.name]?.vehicleEquipmentType === 'LIFT' },
                {
                  label: 'No preference',
                  value: 'NOPREF',
                  checked: !mobilityProfileDetail.creationNeeded && mobilityProfileDetail[item.name]?.vehicleEquipmentType === null
                }
              ]
            }
          ]
        };
      }

      return {
        label: item.label,
        name: item.name,
        checked: !isNullOrUndefined(mobilityProfileDetail?.[item.name]),
        additionalOptions: []
      };
    });

    const mobilityAssistance: Array<CheckboxOption> = [
      {
        name: 'isStairAssistanceRequired',
        label: 'Needs stair assistance (up to three steps)',
        checked: mobilityProfileDetail.isStairAssistanceRequired
      },
      { name: 'hasServiceAnimal', label: 'Travels with a service animal', checked: mobilityProfileDetail.hasServiceAnimal },
      {
        name: 'hasPersonalCareAssistant',
        label: 'Travels with a personal care assistant',
        checked: mobilityProfileDetail.hasPersonalCareAssistant
      }
    ];

    const medicalHealth: Array<CheckboxOption> = [
      { name: 'hasHearingImpairment', label: 'Has hearing impairment', checked: mobilityProfileDetail.hasHearingImpairment },
      { name: 'hasVisionImpairment', label: 'Has visual impairment', checked: mobilityProfileDetail.hasVisionImpairment },
      {
        name: 'hasIntellectualDisability',
        label: 'Has an intellectual or developmental disability',
        checked: mobilityProfileDetail.hasIntellectualDisability
      },
      { name: 'hasPortableOxygen', label: 'Travels with portable oxygen', checked: mobilityProfileDetail.hasPortableOxygen }
    ];

    return [
      {
        label: 'Mobility Aids',
        description: 'Does the rider use any of the following mobility aids when taking rides?*',
        options: mobilityAids,
        isNewForm: mobilityProfileDetail.creationNeeded
      },
      {
        label: 'Mobility Assistance',
        description: 'Does the rider require any of the following accommodations or travel assistance?*',
        options: mobilityAssistance,
        isNewForm: mobilityProfileDetail.creationNeeded
      },
      {
        label: 'Disabilities and Health',
        description: 'Does the rider have any of the following disabilities and/or health conditions?*',
        options: medicalHealth,
        isNewForm: mobilityProfileDetail.creationNeeded
      }
    ];
  }

  convertMobilityProfileSectionToDetail(mobilityProfileSectionForms: Record<number, CheckboxGroupOutput>): MobilityProfileDetail {
    const updatedMobilityProfileDetail = { ...this.mobilityProfileDetail };

    Object.values(mobilityProfileSectionForms).forEach(form => {
      if (form.value && form.value.options) {
        form.value.options.forEach((option: CheckboxOption) => {
          const propertyName = option.name;
          // Update Chair/Scooter objects and their additional options
          if (option.checked) {
            if (['manualWheelchair', 'powerWheelchair', 'electricScooter', 'bariatricWheelchair'].includes(propertyName)) {
              const mobilityAidSelection: { seatTransferAssistanceRequired: boolean; vehicleEquipmentType: string | null } = {
                seatTransferAssistanceRequired: true,
                vehicleEquipmentType: null
              };

              if (option.additionalOptions && option.additionalOptions.length > 0) {
                option.additionalOptions.forEach((additionalOption: AdditionalOption) => {
                  if (additionalOption.name === 'transferToSeat' && additionalOption.value) {
                    // The seat transfer question asks if a rider can transfer unassisted, so "no" means they need assistance
                    mobilityAidSelection.seatTransferAssistanceRequired = additionalOption.value === 'no';
                  }
                  if (additionalOption.name === 'liftOrRamp' && additionalOption.value) {
                    mobilityAidSelection.vehicleEquipmentType = additionalOption.value === 'NOPREF' ? null : additionalOption.value;
                  }
                });
              }
              updatedMobilityProfileDetail[propertyName] = mobilityAidSelection;
            } else {
              updatedMobilityProfileDetail[propertyName] = true;
            }
          } else {
            updatedMobilityProfileDetail[propertyName] = null;
          }
        });
      }
    });

    return updatedMobilityProfileDetail;
  }
}
